import React, { useEffect, useRef, useState } from "react";
import ButtonV2 from "../../shared/components/buttonV2";
import {
  CHEVRON_LEFT,
  EMPTY_SEARCH_2,
  PROFILE_ADD,
  SEARCH_INPUT_ICON,
} from "../../assets";
import PageTitle from "../../shared/components/pagetitle";
import TableComponent from "../../shared/components/table";
import { useQuery } from "react-query";

import { COURT_COLUMN } from "../../shared/components/table/columns";
import { SearchHistoryTypes } from "../../types";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  openCaseModal,
  selectCase,
  selectCaseFormModal,
  selectEditMode,
  setCaseDetailModal,
  setCaseFormModal,
  setDeleteModal,
  setEditMode,
  setSelectedCaseDetails,
  showDeleteModal,
} from "../../states/slices/globalReducer";

import SuccessModal from "../../shared/components/successModal";
import DeleteModal from "../../shared/components/deleteModal";
import { toast } from "react-hot-toast";
import { Loader2 } from "lucide-react";
import {
  deleteCourtCase,
  getCourtAllCases,
} from "../../Services/court.services";
import CourtDetailsModal from "./courtdetailsModal";
import CourtStaffCaseFormModal from "./uploadcasemodal";

function CourtStaffManageCases() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentpage, setCurrentpage] = useState(1);
  const dispatch = useAppDispatch();
  const [uploadCaseModal, setUploadCaseModal] = useState(false);
  const caseFormModal = useAppSelector(selectCaseFormModal);
  const caseDetailModal = useAppSelector(openCaseModal);
  const {
    data: allCases,
    refetch,
    isLoading,
  } = useQuery(["cases", currentpage], () => getCourtAllCases(currentpage));
  const filterCases = allCases?.data?.filter((item: SearchHistoryTypes) =>
    item.title?.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const editMode = useAppSelector(selectEditMode);
  const [deleting, setDeleting] = useState(false);
  const caseDetails = useAppSelector(selectCase);
  const [success, setSuccess] = useState(false);
  const pagesRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const deleteModal = useAppSelector(showDeleteModal);

  useEffect(() => {
    const selectedButton = pagesRefs.current[currentpage];
    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentpage]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await deleteCourtCase(caseDetails?.id);
      if (response) {
        toast.success(response?.message);
        dispatch(setDeleteModal(false));
        refetch();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-3 sm:p-5 w-full">
      <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <PageTitle title="Manage Properties" subtitle="Track all your cases" />
        <ButtonV2
          title={"Upload New Case"}
          image={PROFILE_ADD}
          btnStyle={
            "flex items-center justify-center gap-x-1 bg-globalGold px-3 py-2 rounded-xl flex-row-reverse w-full sm:w-auto"
          }
          textStyle={"text-[#FAFAFA] font-medium text-sm"}
          handleClick={() => {
            setUploadCaseModal(false);
            dispatch(setCaseFormModal(true));
          }}
        />
      </div>

      <div className="w-full mt-6 sm:mt-8 border border-borderColor py-4 rounded-xl">
        <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 gap-4">
          <span className="text-[#2A2E34] font-semibold text-base sm:text-[16px]">
            Cases
          </span>
          <div className="w-full sm:w-fit items-center gap-x-2 flex px-3 py-2 rounded-xl border border-borderColor">
            <img
              src={SEARCH_INPUT_ICON}
              className="w-4 h-4 sm:w-[20px] sm:h-[20px] flex-shrink-0"
              alt=""
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent placeholder:text-sm outline-none"
              placeholder="Search cases"
            />
          </div>
        </div>

        {allCases?.data?.length > 0 ? (
          <div className="mt-2">
            <div className="overflow-x-auto">
              <TableComponent
                DATA={filterCases}
                //@ts-ignore
                COLUMNS={COURT_COLUMN}
              />
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between pt-3 px-3 sm:px-5 gap-4">
              <div className="flex items-center space-x-2 sm:space-x-5">
                <button
                  type="button"
                  onClick={() =>
                    setCurrentpage(allCases?.meta?.current_page - 1)
                  }
                  disabled={allCases?.meta?.current_page === 1}
                  className={`border border-[#EBEBEB] rounded-xl w-8 h-8 sm:w-[40px] sm:h-[40px] flex items-center gap-x-2 justify-center ${
                    allCases?.meta?.current_page === 1 && "opacity-50"
                  }`}
                >
                  <img
                    src={CHEVRON_LEFT}
                    className="w-4 h-4 sm:w-[20px] sm:h-[20px]"
                    alt=""
                  />
                </button>

                <div
                  className={`flex items-center gap-x-1 sm:gap-x-2 ${
                    allCases?.meta?.links?.slice(1, -1).length > 5 &&
                    "max-w-[150px] sm:max-w-[200px] overflow-x-auto"
                  }`}
                >
                  {allCases?.meta?.links
                    ?.slice(1, -1)
                    .map((page: any, index: number) => (
                      <button
                        key={index}
                        ref={(el) => (pagesRefs.current[index] = el)}
                        onClick={() => {
                          page.url && setCurrentpage(Number(page.label));
                        }}
                        className={`flex items-center justify-center pt-[0.5px] hover:bg-globalGold hover:text-white transition-all duration-300 gap-x-3 min-w-[32px] h-[32px] sm:min-w-[40px] sm:h-[40px] rounded-xl ${
                          page.active
                            ? "bg-globalGold text-white"
                            : "border text-[#2A2E34]"
                        }`}
                      >
                        <span className="text-xs sm:text-[13px]">
                          {page.label}
                        </span>
                      </button>
                    ))}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setCurrentpage(allCases?.meta?.current_page + 1)
                  }
                  disabled={
                    allCases?.meta?.current_page === allCases?.meta?.last_page
                  }
                  className={`border border-[#EBEBEB] rounded-xl gap-x-2 w-8 h-8 sm:w-[40px] sm:h-[40px] flex items-center justify-center ${
                    allCases?.meta?.current_page ===
                      allCases?.meta?.last_page && "opacity-50"
                  }`}
                >
                  <img
                    src={CHEVRON_LEFT}
                    className="w-4 h-4 sm:w-[20px] sm:h-[20px] scale-x-[-1]"
                    alt=""
                  />
                </button>
              </div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 color="#9ca3af" size={30} className="animate-spin" />
          </div>
        ) : (
          <div className="flex flex-col gap-y-2 pb-5 justify-center items-center w-full px-4">
            <img
              src={EMPTY_SEARCH_2}
              className="w-24 h-20 sm:w-[147.69px] sm:h-[120px]"
              alt=""
            />
            <span className="text-lg sm:text-xl font-bold text-customDark text-center">
              No Cases Yet
            </span>
            <span className="text-sm font-medium text-primary-50 max-w-sm sm:w-96 text-center">
              You haven't Added any case yet. Click the button below to begin
              your first Case upload.
            </span>
            <button
              onClick={() => setUploadCaseModal(true)}
              className="w-fit hover:scale-95 transition-all duration-300 bg-globalGold px-4 sm:px-5 py-2 rounded-3xl flex items-center justify-center gap-x-2 mt-2"
            >
              <img
                src={PROFILE_ADD}
                className="w-4 h-4 sm:w-[20px] sm:h-[20px]"
                alt=""
              />
              <span className="text-sm sm:text-[16px] font-medium text-white">
                Add New Case
              </span>
            </button>
          </div>
        )}
      </div>

      <CourtDetailsModal
        open={caseDetailModal}
        close={() => dispatch(setCaseDetailModal(false))}
        caseDetails={caseDetails}
      />
      <CourtStaffCaseFormModal
        existingCaseDetails={caseDetails}
        open={caseFormModal || false}
        onSuccess={() => {
          setSuccess(true);
          refetch();
        }}
        close={() => {
          dispatch(setCaseFormModal(false));
          dispatch(setSelectedCaseDetails(null));
        }}
      />

      <SuccessModal
        open={success}
        close={() => setSuccess(false)}
        action={() => {
          refetch();
          setSuccess(false);
          dispatch(setEditMode(false));
        }}
        message={`Case Successfully ${editMode ? "Updated" : "Added"}`}
        message2={`You have successfully  ${
          editMode ? "updated" : "added"
        } a case`}
      />
      <DeleteModal
        open={deleteModal}
        close={() => dispatch(setDeleteModal(false))}
        deleteTitle={"Case Folder"}
        warningMessage={`the case "${caseDetails?.title}"`}
        lastmessage={"cases"}
        handleDelete={handleDelete}
        loading={deleting}
      />
    </div>
  );
}

export default CourtStaffManageCases;
